import { useMemo } from 'react';
import { withStyles, InterestAccount, InterestTerm, InterestRate, Skeleton } from 'vopay';
import styles from './InterestAccountBanner.module.scss';
import { useTranslation } from 'react-i18next';
import { BodyText, MaterialSymbol, UtilButton, Button } from 'UI';
import InterestDisplay from './InterestDisplay';
import InterestDisplaySkeleton from './InterestDisplaySkeleton';

const cn = withStyles(styles);

type InterestAccountBannerProps = {
  standardAccount: InterestAccount;
  premiumAccount: InterestAccount | null;
  interestRates: InterestRate[];
  fetchingInterestAccounts: boolean;
  onEnablePremiumClick: () => void;
  onViewDetailsClick: () => void;
};

export default function InterestAccountBanner({
  standardAccount,
  premiumAccount,
  interestRates,
  fetchingInterestAccounts,
  onEnablePremiumClick,
  onViewDetailsClick,
}: InterestAccountBannerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances.InterestAccountBanner' });

  const standardInterestRate = useMemo(
    () => getInterestRate('short-term', standardAccount.EligibleBalance),
    [standardAccount.EligibleBalance, interestRates],
  );

  const premiumInterestRate = useMemo(
    () => getInterestRate('long-term', premiumAccount?.EligibleBalance || 0),
    [standardAccount.EligibleBalance, interestRates],
  );

  function getInterestRate(term: InterestTerm, balance: number) {
    const applicableRates = interestRates
      .filter((rate) => rate.Term === term)
      .map((rate) => {
        return {
          ...rate,
          RangeStart: rate.RangeStart,
          RangeEnd: rate.RangeEnd,
          AnnualInterestRate: Number(rate.AnnualInterestRate) / 100,
        };
      });

    if (applicableRates.length === 0) {
      // If no applicable rates are found, return 0
      return 0;
    }

    const matchingRate = applicableRates.find((rate) => balance >= rate.RangeStart && balance <= rate.RangeEnd);

    if (!matchingRate) {
      // Check if balance exceeds the last tier's range
      const lastRate = applicableRates[applicableRates.length - 1];

      if (lastRate.RangeEnd === null || balance > lastRate.RangeEnd) {
        return lastRate?.AnnualInterestRate ?? 0;
      }

      // If balance is below the first tier's range, return 0
      return 0;
    }

    return matchingRate?.AnnualInterestRate ?? 0;
  }

  return (
    <div className={cn('interestAccountBanner')}>
      <div className={cn('left')}>
        <BodyText weight="em" className={cn('leftTitle')}>
          <MaterialSymbol icon="auto_graph" size="20px" color="var(--vopayBlue)" />
          {t('title', { defaultValue: 'Interest Accounts' })}
        </BodyText>

        <BodyText className={cn('leftDetails', 'lightText')}>
          {t('desc', { defaultValue: 'Interest is currently active on your account.' })}

          {fetchingInterestAccounts ? (
            <Skeleton inline width={80} />
          ) : (
            <UtilButton onClick={onViewDetailsClick}>{t('viewDetails', { defaultValue: 'View Details' })}</UtilButton>
          )}
        </BodyText>
      </div>

      <div className={cn('right')}>
        <div className={cn('rightSection')}>
          <BodyText size="sm" className={cn('lightText')}>
            {t('standardInterestGained', { defaultValue: 'Standard Interest Gained' })}
          </BodyText>

          {fetchingInterestAccounts ? (
            <InterestDisplaySkeleton />
          ) : (
            <InterestDisplay
              currency="CAD"
              amount={standardAccount.InterestAccrued}
              rate={standardInterestRate}
              balance={standardAccount.EligibleBalance}
              interestRates={interestRates}
              accountType="standard"
            />
          )}
        </div>

        <div className={cn('rightVerticalLine')} />

        <div className={cn('rightSection')}>
          <BodyText size="sm" className={cn('premiumHeading')}>
            {premiumAccount
              ? t('premiumInterestGained', { defaultValue: 'Premium Interest Gained' })
              : t('premiumInterest', { defaultValue: 'Premium Interest' })}
          </BodyText>

          {fetchingInterestAccounts ? (
            <InterestDisplaySkeleton />
          ) : premiumAccount ? (
            <InterestDisplay
              currency="CAD"
              amount={premiumAccount.InterestAccrued}
              rate={premiumInterestRate}
              balance={premiumAccount.EligibleBalance}
              interestRates={interestRates}
              accountType="premium"
            />
          ) : (
            <Button
              size="small"
              colorScheme="feature"
              iconRightProps={{
                icon: 'auto_awesome',
                fillAmount: 1,
              }}
              onClick={onEnablePremiumClick}
            >
              {t('enablePremium', { defaultValue: 'Enable Premium' })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
